import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import Btn from '../../../components/Buttons/Btn';
import MicrosoftLoginBtn from '../../../components/Buttons/MicrosoftLoginBtn/MicrosoftLoginBtn';
import Input from '../../../components/Input';
import { backendErrorNotification } from '../../../components/Notification';
import AuthTypesEnum from '../../../enums/authTypes';
import HttpStatus from '../../../enums/httpStatus';
import LocalStorageKeys from '../../../enums/localStorage';
import { UrlPaths, UrlPathsSuper } from '../../../enums/urlPaths.enum';
import usePersistedState from '../../../hooks/usePersistedState';
import { msalConfig } from '../../../services/auth/microsoft/authMsConfig';
import { getAuthSettingByTenantIdAPI, signInSmartsellApi } from '../../../services/requests';
import { StoreState } from '../../../store/createStore';
import { setStatusLoadingLogin, signInSuccess } from '../../../store/modules/auth/actions';
import LogoutContent from '../../../templates/LogoutContent';
import { AuthSetting } from '../../../types/apiResponse/accessControl';
import { AuthResponseData } from '../../../types/auth';

const SignInTSX = () => {
  const { loadingSigninRequest, isSignin, user } = useSelector((state: StoreState) => state.auth);
  const { t } = useTranslation();
  const dispacth = useDispatch();
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [persistedTenantId, setPersistedTenantId] = usePersistedState<number | null>(
    LocalStorageKeys.TENANT_ID,
    null,
  );

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState<AuthSetting[]>([]);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  const getAuthSettingByTenant = async (tenantIdtoGet: number) => {
    getAuthSettingByTenantIdAPI.query = '';
    const source = getAuthSettingByTenantIdAPI.axios.CancelToken.source();

    try {
      setIsLoading(true);
      const res = await getAuthSettingByTenantIdAPI.show(tenantIdtoGet, source.token);

      if (res.status === HttpStatus.OK) {
        const { data } = res.data;
        if (data) {
          setAuthConfig(data);
        }
      } else {
        throw res;
      }
    } catch (err) {
      if (!getAuthSettingByTenantIdAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSignin) {
      navigate(user?.is_superadmin ? UrlPathsSuper.HOME_SALES : UrlPaths.HOME);
    }
  }, [isSignin, navigate, user?.is_superadmin]);

  const signInSmartsellAction = async () => {
    try {
      dispacth(setStatusLoadingLogin(true));

      const res = await signInSmartsellApi.store({
        login: login,
        password: password,
      });

      if (res.status === HttpStatus.OK) {
        const loginDataSuccessful: AuthResponseData = res.data.data;
        dispacth(signInSuccess(loginDataSuccessful));
      } else {
        throw res;
      }
    } catch (err) {
      if (!signInSmartsellApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      dispacth(setStatusLoadingLogin(false));
    }
  };

  useEffect(() => {
    if (tenantId) {
      const tenantIdNumber = parseInt(tenantId, 10);
      setPersistedTenantId(tenantIdNumber);
      getAuthSettingByTenant(tenantIdNumber);
    }

    if (!tenantId && persistedTenantId) {
      getAuthSettingByTenant(persistedTenantId);
    }
  }, []);

  useEffect(() => {
    const setupMsalInstance = async () => {
      const microsoftConfigData = authConfig?.find(
        (config) => config.auth_type_name === AuthTypesEnum.MICROSOFT && config.active,
      );

      if (microsoftConfigData && microsoftConfigData.data) {
        try {
          const msalConf = await msalConfig(
            microsoftConfigData.data.client_id,
            microsoftConfigData.data.ms_tenant_id,
          );
          const instance = new PublicClientApplication(msalConf);
          setMsalInstance(instance);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Erro ao configurar MSAL:', error);
        }
      } else {
        setMsalInstance(null);
      }
    };

    if (authConfig.length > 0) {
      setupMsalInstance();
    }
  }, [authConfig]);

  const renderSmartsellLoginFields = () => {
    const useSmartsellLogin = authConfig?.some(
      (config) => config.auth_type_name === 'smartsell' && config.active,
    );

    if ((tenantId || persistedTenantId) && !useSmartsellLogin) {
      return <></>;
    }

    return (
      <div>
        <div className="row">
          <div className="itemFormWithLabel">
            <div className="label">{t('login') as string}</div>
            <input
              type="text"
              className="input"
              value={login}
              onChange={(v: any) => setLogin(v.target.value)}
              placeholder={t('enterYourEmailOrYourUsername')}
            />
          </div>
        </div>

        <div className="row">
          <div className="itemFormWithLabel">
            <div className="label">{t('password') as string}</div>
            <Input
              type="password"
              value={password}
              onChange={(v: any) => setPassword(v.target.value)}
              placeholder={t('enterYourPassword')}
              showPswBtn
            />
          </div>
        </div>

        <div className="row">
          <div className="itemForm">
            <Btn
              text={t('enter')}
              style={{ margin: '1.5rem auto', width: '100%' }}
              onClick={signInSmartsellAction}
            />
            <Btn
              text={`${t('forgotPassword')}?`}
              style={{ width: '100%', height: '1rem' }}
              onClick={() => navigate(UrlPaths.FORGOT_PASSWORD)}
              type="link"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderMicrosoftLoginFields = () => {
    if (!msalInstance) {
      return <></>;
    }

    return (
      <MsalProvider instance={msalInstance}>
        <div className="row">
          <div className="itemForm" style={{ display: 'flex', justifyContent: 'center' }}>
            <MicrosoftLoginBtn />
          </div>
        </div>
      </MsalProvider>
    );
  };

  return (
    <LogoutContent title={t('accessAccount')} isLoading={loadingSigninRequest || isLoading}>
      {renderSmartsellLoginFields()}
      {renderMicrosoftLoginFields()}
    </LogoutContent>
  );
};

export default SignInTSX;
